/**
 * The conversation's ONE syntax highlighter, shared by every place the chat paints code.
 *
 * ── WHY A MODULE OF ITS OWN ─────────────────────────────────────────────────────────────────────
 *
 * `ChatCodeBlock` (a fenced block in an answer) and `ChangePreviewCard` (the first lines of a file
 * the run wrote) show code in the same column, a few lines apart. Two highlighter instances with
 * two theme pairs would colour the same Apex keyword two different blues, and a reader would read
 * the difference as meaning something. So the instance, the theme choice, the token-colour reading
 * and the grammar check live here once, and both surfaces call them.
 *
 * Everything in this file was lifted verbatim from `ChatCodeBlock`, where each rule was measured
 * in the browser — the comments say what broke without it. Nothing was re-derived.
 */
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { createCodePlugin } from "@streamdown/code";

/**
 * The chat's own highlighter instance, and the one thing it changes.
 *
 * `@streamdown/code`'s default pair is `["github-light", "github-dark"]`, and github-dark's COMMENT
 * colour is `#6a737d` — 3.26:1 on this block's background, and under 4.5:1 against any ground the
 * theme itself uses, so no background choice fixes it. Measured after the theme-selection fix: one
 * span failing AA, and it was every comment in the answer.
 *
 * `github-dark-default` is GitHub's current palette, with a lighter comment, and it ships with the
 * shiki that is already installed — a theme name, not a dependency. Light stays `github-light`,
 * which measured 4.57:1 at its worst.
 *
 * The markdown pipeline's own `code` plugin is untouched: this instance is used for the `highlight`
 * call below and nothing else, so an unclaimed fence still renders exactly as it did.
 */
export const chatShiki = createCodePlugin({ themes: ["github-light", "github-dark-default"] });

/**
 * The shiki grammar for a language tag, or `null` when there is none.
 *
 * A wrong grammar is worse than none: a language shiki cannot tokenise renders as plain text, and
 * SOQL — which has no grammar of its own — borrows SQL's, close enough to be useful and honest
 * about being generic.
 */
export function highlightLanguage(language: string | null | undefined): string | null {
  const lang = (language ?? "").trim().toLowerCase();
  if (!lang) return null;
  const candidate = lang === "soql" ? "sql" : lang;
  try {
    return chatShiki.supportsLanguage(candidate as never) ? candidate : null;
  } catch {
    return null;
  }
}

export type Token = {
  content: string;
  /** Present when shiki was given ONE theme. */
  color?: string;
  /** Present when it was given a theme PAIR — CSS custom properties, not a colour. */
  htmlStyle?: Record<string, string> | string;
};

/**
 * The colour to paint a token, whichever shape shiki returned.
 *
 * ASKING FOR ONE THEME IS NOT ENOUGH. Passing `themes: [t, t]` still puts shiki in dual-theme mode,
 * where a token carries `htmlStyle` — `{"--shiki-light": "#005cc5", …}` — and no `color`. Seen in
 * the browser: every token rendered, every one of them unstyled, so the block looked exactly as
 * plain as the version this replaced. Both variables hold the same value here, so the first is the
 * colour; `color` is preferred when a single-theme call did return one.
 */
export function tokenColor(token: Token): string | undefined {
  return aaSafe(rawTokenColor(token));
}

function rawTokenColor(token: Token): string | undefined {
  if (token.color) return token.color;
  const style = token.htmlStyle;
  if (!style) return undefined;
  if (typeof style === "string") return /#[0-9a-f]{3,8}\b/i.exec(style)?.[0];
  const first = Object.values(style).find((value) => typeof value === "string" && value.trim());
  return first?.trim();
}

/**
 * The three `github-light` token colours that fail WCAG AA on the chat's light card, mapped to
 * GitHub's CURRENT palette (`github-light-default`) for the very same scopes.
 *
 * Measured by the sweep's contrast probe the first time XML reached the conversation: the tag
 * green `#22863a` reads 4.32:1 on the light card (`<CustomField>`, `<?xml`), under the 4.5:1 AA
 * floor. `#e36209` (variables) and `#d73a49` (keywords) are the other two in that theme below or
 * on the line — 3.5:1 and 4.56:1 on white. `github-light-default` retuned exactly these scopes
 * (`entity.name.tag` → `#116329`, `variable` → `#953800`, `keyword` → `#cf222e`; 7.4:1, 7.4:1,
 * 5.3:1) and left the rest, so this borrows those three values rather than switching the whole
 * theme — whose comment grey `#6e7781` is itself 4.48:1 on white and would trade one failure for
 * another. Keyed by hex, so it is a no-op on every dark-theme colour.
 */
const AA_REPLACEMENTS: Record<string, string> = {
  "#22863a": "#116329",
  "#e36209": "#953800",
  "#d73a49": "#cf222e",
};

export function aaSafe(color: string | undefined): string | undefined {
  if (!color) return color;
  return AA_REPLACEMENTS[color.toLowerCase()] ?? color;
}

/** A stable `undefined` for an uncoloured token, so React does not diff a fresh object per span. */
const NO_STYLE = undefined;
export function colorStyle(color: string | undefined) {
  return color ? { color } : NO_STYLE;
}

/**
 * Which theme is PAINTING the page, and a value that changes when it flips.
 *
 * `resolvedTheme === "dark"` was wrong here, and the failure was invisible at a glance: in dark mode
 * every DEFAULT-foreground token came back as GitHub-light's `#24292e` on a dark block — measured at
 * 1.07:1, with 87 of 134 spans under 3:1 — while keyword and string colours survived because they
 * are legible on both grounds. So the block looked highlighted and its punctuation and identifiers
 * were invisible.
 *
 * `resolvedTheme` is `undefined` until next-themes hydrates, and this transcript is also mounted by
 * the QA harness with no provider above it at all — in both cases it reads as "not dark" while the
 * document is. The `dark` class on the root element is what actually paints the page, and it is
 * what next-themes itself sets, so it is the authority. Legacy reads exactly the same thing for the
 * same reason (`registerAppMonacoThemes(monaco, …classList.contains)`).
 *
 * `resolvedTheme` is still returned, as the RE-RENDER TRIGGER: the class and the value change
 * together on a toggle, and the hook is what tells React to look again.
 */
export function useIsDarkPainted(): { isDark: boolean; themeKey: string | undefined } {
  const { resolvedTheme } = useTheme();
  const isDark =
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : resolvedTheme === "dark";
  return { isDark, themeKey: resolvedTheme };
}

/**
 * Shiki tokens for a block, or `null` while the grammar is still loading.
 *
 * `highlight` returns synchronously once its grammar is in memory and takes a callback for the first
 * call that has to fetch one — hence the state. Rendering unhighlighted text until then is the
 * correct fallback: the block is readable immediately and gains colour a frame later, rather than
 * holding the whole answer back on a network request for a grammar.
 */
export function useShikiTokens(
  codeStr: string,
  language: string | null,
  isDark: boolean,
  /** Not read — only a dependency, so a theme toggle re-tokenises. See `useIsDarkPainted`. */
  themeKey?: string,
): Token[][] | null {
  const [tokens, setTokens] = useState<Token[][] | null>(null);

  useEffect(() => {
    if (!language) {
      setTokens(null);
      return;
    }
    let live = true;
    /*
     * ONE theme, passed twice, chosen from the resolved theme. Shiki's dual-theme mode emits CSS
     * variables per token instead of a colour, which would need a stylesheet contract with the
     * package; a single theme gives a resolvable value and re-resolves when the app theme flips.
     */
    const themes = chatShiki.getThemes();
    const theme = isDark ? themes[1] : themes[0];
    const apply = (value: { tokens?: Token[][] } | null) => {
      if (live && value?.tokens) setTokens(value.tokens);
    };
    try {
      apply(
        chatShiki.highlight(
          { code: codeStr, language: language as never, themes: [theme, theme] },
          apply as never,
        ) as never,
      );
    } catch {
      // A grammar that fails to load is not an error the reader can act on.
      if (live) setTokens(null);
    }
    return () => {
      live = false;
    };
  }, [codeStr, language, isDark, themeKey]);

  return tokens;
}

/**
 * Highlighted lines, or the plain text while the grammar loads.
 *
 * Each line is its own element so a caller can lay lines out — one per row, truncated, numbered —
 * without splitting token spans. `lineClassName` is where a preview puts `truncate`; the fenced
 * block leaves it unset and lets its `pre` scroll instead.
 */
export function HighlightedLines({
  code,
  tokens,
  lineClassName,
}: {
  code: string;
  tokens: Token[][] | null;
  lineClassName?: string;
}) {
  if (!tokens) {
    if (!lineClassName) return <>{code}</>;
    return (
      <>
        {code.split("\n").map((line, i) => (
          <span key={i} className={lineClassName}>
            {line || " "}
          </span>
        ))}
      </>
    );
  }
  return (
    <>
      {tokens.map((line, i) => (
        <span key={i} className={lineClassName}>
          {line.map((token, j) => (
            <span key={j} style={colorStyle(tokenColor(token))}>
              {token.content}
            </span>
          ))}
          {lineClassName ? (line.length ? null : " ") : "\n"}
        </span>
      ))}
    </>
  );
}
